import React from "react";

export enum PageType {
  single = "single",
  multiple = "multiple",
}

// agentType: AgentType | null;
//   schedulingType: SchedulingType | null;
//   schedulingSoftware: SchedulingSoftware | null;
//   patientFinding: PatientFinding | null;
//   patientInfoRetrieval: PatientInfoRetrieval | null;
//   agentName: string | null;
//   callbackNumber: string | null;
//   orgName: string | null;
//   orgAreaCode: string | null;
//   cloudFaxProvider?: CloudFaxProvider | null;

export enum CardTypes {
  agentType = "agentType",
  schedulingType = "schedulingType",
  ehr = "ehr",
  patientFinding = "patientFinding",
  patientInfoRetrieval = "patientInfoRetrieval",
  cloudFaxProvider = "cloudFaxProvider",
}

export type ChoiceCardType = {
  icon: React.JSX.Element;
  title: string;
  description: string;
  nextRoute: string;
  type: CardTypes;
  typeValue: AgentType | SchedulingType | EHR | PatientFinding | PatientInfoRetrieval | CloudFaxProvider;
};

export enum AgentType {
  scheduling = "scheduling",
  confirmation = "confirmation",
  reminder = "reminder",
  waitlist = "waitlist",
  other = "other",
  inbound = "inbound",
}

export const FrontendAgentType = {
  [AgentType.scheduling]: "Scheduling",
  [AgentType.confirmation]: "Confirmation",
  [AgentType.reminder]: "Reminder",
  [AgentType.waitlist]: "Waitlist",
  [AgentType.other]: "Other",
  [AgentType.inbound]: "Inbound",
};

export enum SchedulingType {
  text = "text",
  email = "email",
  phone = "call",
  other = "other",
}

export const FrontendSchedulingType = {
  [SchedulingType.text]: "Text",
  [SchedulingType.email]: "Email",
  [SchedulingType.phone]: "Phone",
  [SchedulingType.other]: "Other",
};

export enum PatientFinding {
  selfReferral = "self-referral",
  referral = "referral",
  other = "other",
}

export const FrontendPatientFinding = {
  [PatientFinding.selfReferral]: "Self-referral",
  [PatientFinding.referral]: "Referral",
  [PatientFinding.other]: "Other",
};

export enum PatientInfoRetrieval {
  spreadsheet = "spreadsheet",
  cloudFax = "cloud-fax",
  other = "other",
}

export const FrontendPatientInfoRetrieval = {
  [PatientInfoRetrieval.spreadsheet]: "Spreadsheet",
  [PatientInfoRetrieval.cloudFax]: "Cloud Fax",
  [PatientInfoRetrieval.other]: "Other",
};

export enum CloudFaxProvider {
  faxPlus = "fax-plus",
  other = "other",
}

export const FrontendCloudFaxProvider = {
  [CloudFaxProvider.faxPlus]: "Fax Plus",
  [CloudFaxProvider.other]: "Other",
};

export type AuthUserModel = {
  agentType: AgentType | null;
  schedulingType: SchedulingType | null;
  enableTexting: boolean;
  enableCalling: boolean;
  ehr: EHR | null;
  patientFinding: PatientFinding | null;
  patientInfoRetrieval: PatientInfoRetrieval | null;
  agentName: string | null;
  callbackNumber: string | null;
  orgName: string | null;
  orgAreaCode: string | null;
  cloudFaxProvider?: CloudFaxProvider | null;
};

export enum SubscriptionPlans {
  freePlan = "Free plan",
}

export enum SubscriptionStep {
  initial = "initial",
  paymentMethod = "paymentMethod",
  selectPlan = "selectPlan",
}

export enum WebsocketActions {
  subscribe = "subscribe",
  unsubscribe = "unsubscribe",
}

export interface StripePlan {
  id: string;
  unit_amount: number;
  recurring: {
    interval: "day" | "week" | "month" | "year";
  };
  metadata: {
    name: string;
    price: string;
    units: string;
  };
}

export type AgentTemplate = {
  templateId: string;
  avatarUrl: string;
  name: string;
  promptId: string;
  purpose: string;
  stripeProductId: string;
  type: AgentType;
  ehr: EHR;
  active: boolean;
};

export type User = {
  userId: string;
  email: string;
  phoneNumber: string;
  lastModified: string; // ISO 8601 format date string
  organizationId: string;
  firstName: string;
  lastName: string;
  freeCalls: number;
  stripeCustomerId: string;
  orgIds?: string[];
  organizations: Organization[];
  superOrganizationId: string;
  superOrgIds?: string[];
  superOrganizations: SuperOrganization[];

  isActive?: boolean;
};

export enum WebsocketEvents {
  calls = "calls",
  scheduledCalls = "scheduledCalls",
  waitlistRuns = "waitlistRuns",
  waitlistSlotStateChanges = "waitlistSlotStateChanges",
  waitlistSlots = "waitlistSlots",
  waitlistPatients = "waitlistPatients",
  patients = "patients",
}

export enum TextWebsocketEvents {
  waitlistSlotStateChanges = "waitlistSlotStateChanges",
  textThreadMessages = "textThreadMessages",
}

export enum StreamType {
  INSERT = "INSERT",
  MODIFY = "MODIFY",
  REMOVE = "REMOVE",
}

export interface PartialFrontendUser {
  email: string;
  firstName: string;
  lastName: string;
  // organizationName?: string;
  // callbackNumber?: string;
  userSub: string;
  password: string;
}

export type FrontendUser = User & {
  token?: string;
};

export enum ScheduledCallStatus {
  scheduling = "scheduling",
  scheduled = "scheduled",
  queued = "queued",
  calling = "calling",
  executed = "executed",

  // error cases
  schedulingError = "scheduling-error",
  queuingError = "queueing-error",
  callingError = "calling-error",
}

type ScheduledCall = {
  scheduledCallId: string;
  agentId: string;
  organizationId: string;
  scheduledDate: string;
  enqueuedDate?: string;
  executedDate?: string;
  redials?: number;
  error?: string;
  schedulingStatus: ScheduledCallStatus;
  overrideUsageLimit?: boolean;

  // essential patient information
  patientPhoneNumber: string;
  patientFirstName: string;
  patientLastName: string;
};

export type FrontendScheduledCall = ScheduledCall & {
  [key: string]: string;
};

export type NewUserData = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  // organizationName: string;
  // callbackNumber: string;
};

export type TemporaryUserData = {
  // not saved in the database! Just used for authentication
  email: string;
  password: string;
};

export interface AuthState {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  user: FrontendUser | null;
  authUserModel: AuthUserModel | null;
  message: string;
  signUpResult?: SignUpResult; // Add this line
}

export interface SignUpResult {
  email: string;
  status: string; // 'Pending Verification'
}

export type WaitlistTextState =
  | WaitlistSlotState.notContacted
  | WaitlistSlotState.accepted
  | WaitlistSlotState.declined
  | WaitlistSlotState.bookingFailed
  | WaitlistSlotState.cannotContact
  | WaitlistSlotState.contacted
  | WaitlistSlotState.inProgress
  | WaitlistSlotState.noResponse
  | WaitlistSlotState.unknown
  | WaitlistSlotState.error;

export type WaitlistCallState =
  | WaitlistSlotState.notContacted
  | WaitlistSlotState.accepted
  | WaitlistSlotState.declined
  | WaitlistSlotState.bookingFailed
  | WaitlistSlotState.cannotContact
  | WaitlistSlotState.noResponse
  | WaitlistSlotState.unknown
  | WaitlistSlotState.error
  | WaitlistSlotState.noLongerAvailable
  | WaitlistSlotState.callQueued
  | WaitlistSlotState.callInProgress;

export type WaitlistSlot = {
  waitlistRunId: string;
  waitlistSlotId: string;
  patientId: string;
  state: WaitlistSlotState;
  stateJustification: string;
  callEnabled: boolean;
  textEnabled: boolean;
  textId?: string;
  callId?: string;
  callState?: WaitlistCallState;
  callStateJustification?: string;
  textState?: WaitlistTextState;
  textStateJustification?: string;
  patient: Patient;
};

export enum WaitlistSlotState {
  notContacted = "not-contacted",
  accepted = "accepted",
  declined = "declined",
  bookingFailed = "booking-failed", // failed to book an appointment (e.g. already booked)
  cannotContact = "cannot-contact", // patient does not want to be contacted
  tooManyRuns = "too-many-runs", // patient has been contacted too many times

  contacted = "contacted", // initial text (or voicemail for hybrid campaigns)
  inProgress = "in-progress", // received at least one response text
  unknown = "unknown", // Call with unknown outcome (call-only campaign), or waitlist run expired with a response but the outcome was not clear
  noResponse = "no-response", // Call went to voicemail (call-only campaign), or waitlist run expired without response
  error = "error",
  expired = "expired",
  noLongerAvailable = "no-longer-available",
  callQueued = "call-queued",
  callInProgress = "call-in-progress",
}

export type WaitlistSlotStateChange = {
  waitlistSlotStateChangeId: string; // primary key, not useful for anything
  waitlistSlotId: string;
  state: WaitlistSlotState;
  stateJustification: string;
  textThreadMessageId: string;
  createdAt: string;
  patientId: string;
};

export type WaitlistRun = {
  waitlistRunId: string;
  organizationId: string;
  agentId: string;
  appointmentDate: string;
  state: WaitlistState;
  stateJustification: string;
  waitlistSlotIds: string[];
  bookedBy?: Patient;
  callEnabled: boolean;
  textEnabled: boolean;
  expirationDate: string;

  ehrProviderId?: string;
  pcpName?: string;
  ehrApptTypeId?: string;
  appointmentTypeName?: string;
  appointmentTypeDuration?: number;
  ehrContactType?: string;
  location?: string;
  waitlistRunBatchId?: string;

  // type
  ehr: EHR;

  timeToFillMinutes?: number;
  slots: WaitlistSlot[];
  createdAt: string;
  lastModified: string;

  waitlistDebug?: string;
};

export enum WaitlistTextStates {
  firstMessageSent = "first-message-sent",
  inProgress = "in-progress",
  accepted = "accepted",
  declined = "declined",
  notContacted = "not-contacted",
  expired = "expired",
}

export type TextThreadMessage = {
  textThreadMessageId: string;
  date: string;
  message: string;
  role: "agent" | "patient" | "clinic" | "network" | "tool"; // TODO (Hizami): "clinic" role is for manual override
  patientPhoneNumber: string;
  agentPhoneNumber: string;
  phoneNumbersKey: string; // key of the form `${agentPhoneNumber}-${patientPhoneNumber}` for easy filtering

  organizationId: string;
  agentId: string;
  patientId: string;

  error?: string;
  sid?: string;
};
export type FrontendText = {
  textId: string;
  organizationId: string;
  waitlistRunId: string;
  thread: TextThreadMessage[];
  patientPhoneNumber: string;
  agentPhoneNumber: string;
  variables: Record<string, unknown>;
  state: string;
  stateJustification: string;
  waitlistPatientId: string;
  manualOverride: boolean;
};

export type Organization = {
  adminId: string;
  organizationId: string;
  superOrganizationId: string;
  name: string;
  integrations: EHR[];
  ehr: EHR;
  timezone: string;
  phoneNumber: string;
  textingPhoneNumber: string;
  createdAt: string; // ISO 8601 format date string
  lastModified: string;
  inboundRedirectNumber: string; // E.164 format
  emergencyTransferNumber: string;
  workingHoursStart: string;
  workingHoursEnd: string;
  logoUrl: string;
  patientGroupIds: string[];
  patientGroups: PatientGroup[];
  stripeConnectedAccountId?: string;

  autoAddEnabled?: boolean;
  autoDischargeEnabled?: boolean;
  skipDischargeIfActiveCaseExists?: boolean;

  ignoreFccHours?: boolean;
  webptPollingTimestamps?: Record<number, string>;
  remindersLastTriggeredAt?: string;

  autoAddMultiCaseStudy?: boolean;
  useMultiAuthSource?: boolean;

  // When enabled, patients can book same-day appointments across different
  // case studies, as long as the providers are in different provider groups.
  allowSameDayAppointmentsAcrossProviderGroups?: boolean;
};

export type PatientGroup = {
  patientGroupId: string;
  organizationId: string;
  name: string;
};

export enum OnboardingStep {
  init = 0,
  orgInfo = 1,
  createAgent = 2,
  ehrToken = 3,
  makeCall = 4,
  end = 5,
}

export enum CalendlyStatus {
  connected = "connected",
  inProgress = "in-progress",
  notFound = "not-found",
}

export type CancellationFee = {
  window: "Always" | number;
  amount: number;
  processingFee: number;
  policyUrl?: string;
};

export type Agent = {
  agentId: string;
  agentType: AgentType;
  isActive: boolean;
  language: string;
  organizationId: string;
  voiceId: string;
  purpose: string;
  outcomes: string[];
  avatarFilename: string;
  createdAt: Date;
  lastModified: string;
  title: string;
  description: string;
  provider: "vapi" | "retell" | "bland";
  waitlistColumnRankings?: string[];
  variableSubRankings?: Record<string, string[]>;

  areaCode: string;

  // Location FAQ
  address?: string;
  workingHours?: string;

  // Organization Communication Settings
  emailUpdatesTo?: string;

  // Prompt variables
  promptId: string;
  intro?: string;
  welcomeText?: string;
  firstText?: string;
  appointmentAvailableText?: string;
  appointmentBookedText?: string;
  appointmentTakenText?: string;
  planOfCareCreatedText?: string;
  planOfCareUpdatedText?: string;
  planOfCareAutomatedText?: string;
  initialExamText?: string;
  dischargedText?: string;
  autoDischargedText?: string;
  reminderText?: string;
  appointmentCancelledText?: string;
  checkedInPocLinkText?: string;

  // Prompt variables (Spanish translations, used when the patient's language is Spanish)
  welcomeTextEs?: string;
  appointmentAvailableTextEs?: string;
  appointmentBookedTextEs?: string;
  appointmentTakenTextEs?: string;
  planOfCareCreatedTextEs?: string;
  planOfCareUpdatedTextEs?: string;
  planOfCareAutomatedTextEs?: string;
  initialExamTextEs?: string;
  checkedInPocLinkTextEs?: string;
  dischargedTextEs?: string;
  reminderTextEs?: string;
  appointmentCancelledTextEs?: string;
  autoDischargedTextEs?: string;

  // Communication settings
  enableTexting: boolean;
  enableCalling: boolean;

  // Text message settings
  enableAutomaticTextResponses: boolean;

  // Phone call settings
  phoneNumber: string;
  spamRiskPrevention: boolean;
  callerId: string | null;
  enableRedialing: boolean;
  maxDials: number;
  redialGapMinutes: number;

  // Messaging settings
  name: string;
  enableLeavingVoicemail: boolean;

  // Waitlist settings
  minutesToCutoffBeforeAppointment?: number;
  minutesToWaitBeforeCalling?: number;
  autoStartWaitlistRuns: boolean;
  daysForwardToCheckAvailableSlots: number;
  minutesBetweenWaitlistGroups?: number;
  minutesBetweenPatients?: number;
  randomPriority?: boolean;
  maxRunsPerPatient?: number;
  maxRunsPerPatientPerDay?: number;
  maxAppointmentsPerWeek?: number;
  gapBetweenAppointments?: number;
  disableInactivityPause?: boolean; // when true, patients are never paused from waitlist texts due to prolonged inactivity

  minutesToWaitBeforeStarting?: number;

  omitNoLongerAvailableText?: boolean;
  omitSameDayAppointments?: boolean;
  patientAdjustAvailability?: boolean;
  patientSelfSchedule?: boolean;
  ignoreApptType?: boolean;
  usingWebptReminders?: boolean;
  reminders?: boolean;
  reminderTime?: string;
  reminderDaysBeforeAppointment?: number;

  interruptionThreshold?: number;

  defaultAppointmentDuration?: number;

  // Plan of Care settings
  singleBookInsuranceIds?: string[];
  automatedPocEnabled?: boolean;
  pocSendDayOfWeek?: number;
  pocSendTime?: string;
  pocAutoSendNextWeek?: boolean;
  addToWaitlistForUnscheduledVisitsOnPocCreated?: boolean;

  // Discharge settings
  deleteFutureAppointmentsOnDischarge?: boolean;

  // Initial Visit settings
  initialVisitBookingLeadTime?: number;
  autoAddPatientsAfterIE?: boolean;
  autoAssignPOCAfterIE?: boolean;
  templatePlanOfCareId?: string;
  autoSendPOCLinkAfterIE?: boolean;

  // Texting functions
  enableCancelAppointments?: boolean;
  enableReminderConfirmation?: boolean;
  pushReminderConfirmationToWebpt?: boolean;
  chargeCancellationFee?: boolean;
  cancellationFee?: CancellationFee;
  noCancellationWindowHours?: number;

  enableCancellationRescheduleOutreach?: boolean;

  ehr: EHR;

  avatarUrl: string;

  stripeProductId: string;

  justDay: boolean; // whether or not to include the time in the appointment date

  // conversational pathway
  conversationalPathwayId?: string;
} & Partial<AgentStats> &
  Partial<BookingStats>;

export type AgentStats = {
  bookedApptVolume: number;
  bookedApptRate: number;
  avgTimeToFillAppt: number;
};

export type BookingStats = {
  waitlistAppts: number;
  pocAppts: number;
  initialExamAppts: number;
};

export enum EHR {
  webpt = "webpt",
  none = "none",
}

export enum CallStatuses {
  calling = "calling",
  complete = "complete",
  error = "error",
  voicemail = "voicemail",
  /*
  new = "new",
  queued = "queued",
  allocated = "allocated",
  started = "started",
  complete = "complete",
  pre_queue_error = "pre_queue_error",
  queue_error = "queue_error",
  call_error = "call_error",
  complete_error = "complete_error",
  */
}

export type Call = {
  startedAt: string;
  endedAt: string;
  callLength: number;
  to: string;
  from: string;
  inbound: boolean;
  errorMessage: string | null;
  answeredBy: string;
  summary: string;
  callEndedBy: string;
  callId: string;
  patientFirstName: string;
  patientLastName: string;
  patientEmail: string;
  patientPhoneNumber: string;
  outcome: string;
  outcomeSentiment: OutcomeSentiment; // "positive" | "negative" | "neutral"
  outcomeJustification: string;
  status: CallStatuses;
  outcomeDetails: string;
  transcript: string;
  appointmentDate: string;
  justDay: boolean; // whether to include the time in the appointment date
  recordingUrl: string;
  agentId: string;
  scheduledCallId: string;
  scheduledDate?: string;

  // for frontend only
  agentName: string;

  direction: "inbound" | "outbound";

  visitReason?: string;
  cancellationReason?: string;

  actionItem?: string;
  actionItemReason?: string;
  patientRequest?: string;
  patientRequestDetails?: string;
};

export enum OutcomeSentiment {
  positive = "positive",
  negative = "negative",
  neutral = "neutral",
}

export type RawWebptPatient = {
  webptPatientId: number;
  firstName: string;
  lastName: string;
  providerName: string;
  dob: string;
};

export type OmniPatient = {
  patientId: string;
  patient: Patient;
  waitlist: WaitlistPatient;
  [EHR.webpt]?: WebptPatient;
  //[EHR.none]?: EHRPatient;
};

export type PartialOmniPatient = {
  patientId: string;
  patient: Partial<Patient>;
  waitlist: Partial<WaitlistPatient>;
  [EHR.webpt]?: Partial<WebptPatient>;
};

/*
export type EHRPatient = {
  patientId: string; // primary key
  organizationId: string;

  ehrPatientId: string; // the id of the patient on the EHR
  ehrProviderIds: string[];
  ehrAppointmentTypeIds: string[];

  duration?: number;
};
*/

export type WebptPatient = {
  patientId: string;
  organizationId: string;
  webptPatientId: number;
  webptProviderIds: string[];
  providerGroupId?: string;
  webptAppointmentDuration: number;
  webptCaseStudyId: number;
  removalWindow?: number;
  webptAppointmentTypeId?: string;
  initialEvalAppointmentTypeId?: string | null;

  ehrPatientId?: undefined; // hack to bypass typing restrictions
  ehrProviderIds?: undefined;
  ehrAppointmentTypeIds?: undefined;
};

export enum Consent {
  given = "given",
  revoked = "revoked",
}

export type Patient = {
  patientId: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  phoneNumber: string;
  email?: string;
  timezone: string;
  language: string;
  callEnabled: boolean;
  textEnabled: boolean;
  deletedTimestamp?: string; // ISO 8601 UTC format date string
  dob?: string;
  consentHistory: {
    consent: Consent;
    message: string;
    date: string;
    threadMessageId: string | null;
  }[];
  insuranceId?: string;
  createdAt?: string;
  waitlistPausedNotifiedAt?: string | null; // ISO 8601 UTC date string; set when the patient is notified that waitlist texts are paused due to prolonged inactivity, cleared when the patient responds again
  lastRespondedAt?: string; // ISO 8601 UTC date string; timestamp of the patient's most recent inbound text, denormalized so recency checks (e.g. waitlist inactivity) don't require querying text thread history
};

export type WaitlistPatient = {
  patientId: string; // primary key
  organizationId: string;
  availableDates: AvailableDate[];
  priority: number;
  archived?: boolean;
  archivedAt?: string | null;
  patientGroupId?: string | null;
  maxAppointmentsPerWeek?: number;
  gapBetweenAppointments?: number;
};

export type CSVPatient = {
  patientFirstName: string;
  patientLastName: string;
  patientPhoneNumber: string;
  scheduledDate?: string;
};

export type ApiKey = {
  key: string | undefined;
  name: string; // names are unique for each org
};

export type Webhook = {
  url: string;
  signingKey: string | undefined;
  name: string; // names are unique for each org
  active: boolean;
  recordingEnabled: boolean;
};

export type VariableDetail = EssentialVariableDetail | AuxillaryVariableDetail | SpecialVariableDetail;

export type AuxillaryVariableDetail = {
  required: boolean;
  type: AuxillaryVariableTypes;
  metadata: Record<string, string | number>;
};

export type EssentialVariableDetail = {
  required: true;
  type: EssentialVariables;
  metadata: Record<string, string | number>;
};

export type SpecialVariableDetail = {
  required: false;
  type: SpecialVariables;
  metadata: Record<string, string | number>;
};

export type AuxillaryVariableTypes = SingleTypes | ArrayTypes;

export enum SpecialVariables {
  liveTransferNumber = "live_transfer_number",
  patientFirstName = "patient_first_name",
  patientLastName = "patient_last_name",
  preferredContactMethod = "preferred_contact_method",
  preferredLanguage = "preferred_language",
  email = "email",
}

export enum EssentialVariables {
  patientPhoneNumber = "patient_phone_number",
}

export enum SingleTypes {
  date = "date",
  phoneNumber = "phone_number",
  string = "string",
  number = "number",
}

export enum ArrayTypes {
  dateArray = "date[]",
  phoneNumberArray = "phone_number[]",
  stringArray = "string[]",
  numberArray = "number[]",
}

export type QuestionAnswerPair = {
  id: string;
  question: string;
  answer: string;
  source?: string;
};

export enum WaitlistState {
  notStarted = "not-started",
  ongoing = "ongoing",
  accepted = "accepted",
  declined = "declined",

  bookingFailed = "booking-failed",
  noValidPatients = "no-valid-patients",
  expired = "expired",
}

export enum LogType {
  appointmentBooked = "appointment-booked",
  bookingFailed = "booking-failed",
  //acceptDetected = "accept-detected",
  //declineDetected = "decline-detected",
  textReceived = "text-received",
  textSent = "text-sent",
  actionItem = "action-item",
  aiError = "ai-error",
  updateAvailability = "update-availability",
  viewAppointments = "view-appointments",
  appointmentCancelled = "appointment-cancelled",
  lateCancelFeePaid = "late-cancel-fee-paid",
  initialVisitLinkSent = "initial-visit-link-sent",
  initialVisitVisitBooked = "initial-visit-visit-booked",
  planOfCareSent = "plan-of-care-sent",
  joinedWaitlist = "joined-waitlist",
  planOfCareVisitBooked = "plan-of-care-visit-booked",
  planOfCareScheduled = "plan-of-care-booked",
  textNotDelivered = "text-not-delivered",
  reminderConfirmed = "reminder-confirmed",
}

export type LogItem = {
  createdAt?: string;
  organizationId: string;
  agentId: string;
  patientId?: string;
  logItemId: string;
  logType: LogType;
  logDescription: string;
  completed?: boolean;
  textThreadMessageId?: string;
};

export type TimeInterval = {
  start: string;
  end: string;
};

export type DateRange = {
  startDate: string;
  endDate: string;
};

export type AvailableDate = string | TimeInterval | DateRange;

export enum RequestStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  DENIED = "DENIED",
  INCOMPLETE = "INCOMPLETE",
}

export type WaitlistRequest = {
  requestId: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  status: RequestStatus;
  createdAt: string;
  patientId: number;
  ehr: EHR;
};

export enum NotificationType {
  patientAutoAddFailed = "patient-auto-add-failed",
  welcomeTextFailed = "welcome-text-failed",
}

export type NotificationItem = {
  createdAt?: string;
  superOrganizationId: string;
  organizationId: string;

  notificationId: string;
  notificationType: NotificationType;
  message: string;

  completed: boolean;
};

export type CompleteTemplatePlanOfCare = TemplatePlanOfCare & {
  templatePlanOfCareWeeks: CompleteTemplatePlanOfCareWeek[];
};

export type CompleteTemplatePlanOfCareWeek = TemplatePlanOfCareWeek & {
  templatePlanOfCareAppointments: TemplatePlanOfCareAppointment[];
};

export type TemplatePlanOfCare = {
  superOrganizationId: string;
  organizationId: string;

  templatePlanOfCareId: string;
  name: string;
  numAppointments: number; // total number of appointments in the plan of care
  gap: number; // number of days between appointments

  templatePlanOfCareWeekIds: string[]; // array of templatePlanOfCareWeekIds
};

export type TemplatePlanOfCareWeek = {
  superOrganizationId: string;
  organizationId: string;

  templatePlanOfCareId: string;
  templatePlanOfCareWeekId: string;

  templatePlanOfCareAppointmentIds: string[]; // array of templatePlanOfCareAppointmentIds
};

export type TemplatePlanOfCareAppointment = {
  superOrganizationId: string;
  organizationId: string;

  templatePlanOfCareId: string;
  templatePlanOfCareWeekId: string;
  templatePlanOfCareAppointmentId: string;

  appointmentTypeId: string;
};

export type CompleteActivePlanOfCare = ActivePlanOfCare & {
  activePlanOfCarePeriods: CompleteActivePlanOfCarePeriod[];

  extraAppointments?: any[]; // webpt appointments that weren't mapped onto the plan of care
};

export type CompleteActivePlanOfCarePeriod = ActivePlanOfCarePeriod & {
  activePlanOfCareAppointments: ActivePlanOfCareAppointment[];

  extraAppointments?: any[]; // webpt appointments that weren't mapped onto the plan of care
};

export type ActivePlanOfCare = {
  activePlanOfCareId: string;

  superOrganizationId: string;
  organizationId: string;
  patientId: string;

  name: string;
  numAppointments: number;
  gap: number;
  frequency?: number;

  lastOpened?: string; // ISO 8601 format date string
  lastManualEdit?: string; // ISO 8601 format date string
  lastSent?: string; // ISO 8601 format date string

  caseStudyName?: string;
  discharged?: boolean;

  defaultAppointmentTypeId?: string;

  activePlanOfCarePeriodIds: string[]; // array of activePlanOfCarePeriodIds

  totalAppointments?: number;
  totalAppointmentsInPast?: number;
  appointmentsOccurred?: number;
  appointmentsMissed?: number;
  appointmentsScheduled?: number;
  appointmentsUnscheduled?: number;
  appointmentsUnauthorized?: number;
  totalExtraAppointments?: number;

  authorizationUsed?: number;
  authorizationTotal?: number;
  authorizationExpires?: string; // ISO 8601 format date string

  startDate?: string;
  endDate?: string;

  ignoreAuthorization?: boolean;
  ignoreDocumentation?: boolean;

  caseStudyId?: number;

  webptProviderIds?: string[];
  providerGroupId?: string;

  lastModified?: string; // ISO 8601 format date string
  createdAt?: string; // ISO 8601 format date string

  preserve?: boolean; // whether to make auto-adjustments to it
};

// currently a period is a week
export type ActivePlanOfCarePeriod = {
  activePlanOfCareId: string;
  activePlanOfCarePeriodId: string;

  superOrganizationId: string;
  organizationId: string;
  patientId: string;

  periodStart: string;
  periodEnd: string;

  startDate: string;
  endDate: string;

  activePlanOfCareAppointmentIds: string[]; // array of activePlanOfCareAppointmentIds

  preserve?: boolean; // whether to make auto-adjustments to it
};

export type ActivePlanOfCareAppointment = {
  activePlanOfCareId: string;
  activePlanOfCarePeriodId: string;
  activePlanOfCareAppointmentId: string;

  superOrganizationId: string;
  organizationId: string;
  patientId: string;

  appointmentTypeId: string;
  appointmentDate?: string; // only for appointments that have already been scheduled
  status: "occurred" | "scheduled" | "unscheduled" | "missed" | "unauthorized";

  notes?: string;
};

export enum AppointmentBookingMethod {
  whitespace = "whitespace",
  availabilityBlocks = "availability-blocks",
  whitespaceWithAvailableBlocks = "whitespace-with-available-blocks",
}

export enum AppointmentCategory {
  block = "block",
  followUp = "follow-up",
  available = "available",
  ignore = "ignore",
  evaluation = "evaluation",
}

export type AppointmentType = {
  superOrganizationId: string;
  appointmentTypeId: string;
  ehrAppointmentTypeId: string;
  name: string;
  label?: string; // label to display to the user
  duration: number; // duration in minutes (default)
  visibleDuration?: number; // duration in minutes to display to patient. Falls back to `duration` when unset.
  category?: AppointmentCategory;
  availableAppointmentTypeIds?: string[];
  transparency?: boolean;
  exclusive?: boolean;
  keyword?: string | string[];
  appointmentNotesKeyword?: string | string[];
  insuranceIds?: string[];
  organizationIds?: string[];
  isOneOnOne?: boolean;

  webptShortCode?: string; // i.e. follow-up is "DN", all custom types are "CT"
};

export enum RawWebptAppointmentStatusAbbreviation {
  none = "XX", // ""
  confirmed = "Conf", // "Confirmed"
  checkedIn = "ChIn", // "Checked-In"
  checkedOut = "COut", // "Checked-Out"
  cancelled = "Canc", // "Cancelled"
  noShow = "NoSh", // "No Show"
}

export type WebptPatientAppointment = {
  id: number;
  ev_id: number;
  Event_EventID: number;

  PatientID: number;

  //dtstart: string; // i.e. '2025-01-09 13:40:00'
  //dtend: string; //  i.e. '2025-01-09 13:40:00'
  startString: string;
  endString: string;

  calendar_id: number;
  Cal_Title: string; // i.e. "Nicole Sabes (SF)"

  CaseID: number;
  CaseTitle: string; // i.e. "Lumbar"

  summary: string;
  description: string;
  status: string; // i.e. "A"
  created: string; //  i.e. '2025-01-09 13:40:00'
  lastmodified: string; //  i.e. '2025-01-09 13:40:00'

  apptStatus: RawWebptAppointmentStatusAbbreviation; // i.e. "ChIn"
  AptType: string; // legacy type, i.e. follow up is "DN"
  visit: number;

  Event_TreatingTherapistID: number;
  Event_TreatingFacilityID: number;
  reminderStatus: string; // "None" or "Sent" or "Scheduled"
};

export const WEBPT_INSURANCE_TYPES = {
  "1": "HMO",
  "2": "PPO",
  "3": "Medicare",
  "4": "EPO",
  "5": "Self Pay",
  "6": "Medicaid",
  "7": "WCOMP",
  "8": "Tricare",
  "9": "Commercial",
  "10": "Auto/PIP",
  "11": "POS",
  "12": "GEHA",
  "13": "VA",
  "14": "Employer",
  "15": "Other",
  "16": "Medicare Replacement",
  "17": "Blue Cross Blue Shield",
} as const;

export type AuthorizationSource = "prescription" | "authorization" | "policy";

export type InsuranceType = {
  insuranceTypeId: string;
  superOrganizationId: string;
  ehrInsuranceTypeId: string;
  name: string;

  limitPerHour?: number; // sportsmed parameter
  defaultAllowedVisits?: number;
  defaultAllowedVisitsOverrideThreshold?: number; // allowed_visits value that triggers defaultAllowedVisits override
  authorizationSourcePriority?: AuthorizationSource[];
};

export type Insurance = {
  superOrganizationId: string;
  insuranceId: string;
  name: string;
  ehrInsuranceId: string;
  ehrInsuranceTypeId?: string;
  byPassAuthorization?: boolean;
  notAutoAdd?: boolean;
  noCancelFee?: boolean;
  authorizationSourcePriority?: AuthorizationSource[];
};

export type Provider = {
  providerId: string;
  organizationId: string;
  ehrProviderId: string;
  providerName: string;
  isSupervisingProvider: boolean;
  maxSimultaneousPatients: number;
  minGapBetweenVisits: number;
  bookableTimeRanges: BookableTimeRanges;
  insurancesAccepted: string[];
  appointmentTypesAccepted: string[];
};

export type ProviderGroup = {
  providerGroupId: string;
  superOrganizationId: string;
  organizationId: string;
  name: string;
  providerIds: string[];
  primaryProviderIds: string[]; // auto-assign patients to this group if one of these providers did the eval
  keyword?: string | string[]; // case-insensitive substring(s) to match against case study name for auto-assignment
};

export type TimeRange = {
  startTime?: string; // 24-hour format HH:mm
  endTime?: string; // 24-hour format HH:mm
};

export type BookableTimeRanges = {
  monday?: TimeRange[];
  tuesday?: TimeRange[];
  wednesday?: TimeRange[];
  thursday?: TimeRange[];
  friday?: TimeRange[];
  saturday?: TimeRange[];
  sunday?: TimeRange[];
};

export type WebptPlanOfCareMetadata = {
  id: number;
  caseStudyId: number;
  caseStudyName: string;
  numWeeks: number;
  numPerWeek: number;
  rawFrequency: string;
  numVisits: number;
  totalAuthorizedVisits: number;
  usedAuthorizedVisits: number;
  numFutureScheduledVisits: number;
  expirationDate: string;
  startDate: string | null;
  endDate: string | null;
  authorizationsPerSource?: {
    prescriptions: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
    authorizations: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
    policies: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
  };
};

export type RawWebptCalendar = {
  Cal_CalendarID: number;
  Cal_Owner_UserID: number; // id of owner of calendar
  Usr_FullName: string; // name of owner of calendar
  Cal_Type: string;
  Cal_Title: string; // name of calendar
  Cal_Status: string; // i.e. "active"
  CalAcl_Access: string; // i.e. "write"
};

export type RawWebptAvailableCalendar = {
  Cal_CalendarID: number;
  Cal_Title: string;
  Cal_Summary: string | null;
  CalAcl_Access: string; // i.e. "admin"
  CalAcl_Access_Public: string; // i.e. "write"
  booUserHasCalendar: boolean; // set to true on webpt to make the calendar viewable in the scheduler
};

export type SuperOrganization = {
  superOrganizationId: string;
  organizationIds: string[];
  name: string;

  defaultGapBetweenAppointments?: number;
  autoExtendToWeeks?: boolean;
  numberOfWeeksToAutoExtend?: number;
  autoExtendToAuth?: boolean;
  autoExtendToPoc?: boolean;
  autoTruncateAppointments?: boolean;

  stripeConnectedAccountId?: string;

  ignorePreferredName?: boolean;

  appointmentBookingMethod?: AppointmentBookingMethod;
};

export enum QueuePatientState {
  waiting = "waiting",
  importing = "importing",
  sendingWelcomeText = "sending-welcome-text",
  creatingPlanOfCare = "creating-plan-of-care",
  sendingPocText = "sending-poc-text",
  added = "added",
  aborted = "aborted",
}

// for auto-add patients in queue
export type QueuePatient = {
  queuePatientId: string;
  superOrganizationId: string;
  organizationId: string;

  patientId: string; // used to trace the state of creation
  name: string;
  webptPatientId: number;
  state: QueuePatientState;
  done: boolean;
  error?: string | null;
  createdAt: string;
};

export type LateCancellationFee = {
  lateCancellationFeeId: string;
  patientId: string;
  cancelledAppointmentDate: string;
  providerName: string;
  cardLast4: string;
  stripeTransactionId: string;
  amount?: number;
  createdAt: string;
};
